import { EventModel } from 'features/events/EventModel';
import { liveEventSelector } from 'features/events/eventsSelectors';
import { useDispatch } from 'react-redux';
import { useEffect, useContext, useRef } from 'react';
import { useLocation, useNavigate, matchPath } from 'react-router-dom';
import { UserAgentContext } from 'features/user-agent/userAgent';
import { useAudio } from 'features/audio/audio';
import { useAppSelector } from 'hooks/redux';
import { useIsEventLive } from '../eventHooks';
import { WebSocketContext } from 'features/hub/hub';
import { navigateToEvent } from '../thunks';

export const useChannelLiveEventNavigation = (event: EventModel | null) => {
  const navigate = useNavigate();
  const liveEvent: EventModel | null = useAppSelector(liveEventSelector);
  const { pathname } = useLocation();
  const { stop, loadAudio } = useAudio();
  const { isCreatorAppWebview, isBrowserEmbed, isListenerAppWebview } =
    useContext(UserAgentContext);
  const currentEventIsLive = useIsEventLive(event);
  const dispatch = useDispatch();
  const channelFallbackEnabled = useAppSelector(
    (state) => state.channel.channelDetails?.fallbackEnabled
  );
  const { subscribeToBroadcast } = useContext(WebSocketContext);
  const fallbackEventLive = useIsEventLive(liveEvent);

  const valuesRef = useRef({
    event,
    liveEvent,
    stop,
    loadAudio,
    navigate,
    subscribeToBroadcast
  });
  valuesRef.current = {
    event,
    liveEvent,
    stop,
    loadAudio,
    navigate,
    subscribeToBroadcast
  };

  // Tracks the live event id we have already dispatched a navigation to but
  // not yet "arrived" at. Even with primitive deps, there's a render window
  // mid-transition where pathname has updated but `event` hasn't caught up
  // (useEventFetcher fetches async) — without this guard the effect re-fires
  // and double-dispatches navigateToEvent for the same target.
  const navigatingToIdRef = useRef<string | null>(null);

  const eventId = event?.id;
  const eventIsUpcoming = event?.isUpcoming() ?? false;
  const liveEventId = liveEvent?.id;
  const liveEventIsFallback = liveEvent?.fallback ?? false;

  useEffect(
    function redirectToCurrentLiveEvent() {
      // Only support this on channels with fallback
      if (!channelFallbackEnabled) return;
      // Don't redirect if a scheduled upcoming event and it's not ended
      if (eventIsUpcoming) return;
      // Ignore any redirects on the creator apps
      if (isCreatorAppWebview || isBrowserEmbed) return;
      // If going from live event -> Fallback
      // wait for the audio to finish first
      if (liveEventIsFallback && currentEventIsLive) return;
      // Don't redirect to the fallback if it's not live yet
      if (liveEventIsFallback && !fallbackEventLive) return;

      // We've arrived — clear the guard so the next transition can fire.
      if (eventId && eventId === liveEventId) {
        navigatingToIdRef.current = null;
        return;
      }

      // Only navigate if we have a live event and
      // it isn't the current displayed event
      if (liveEventId && eventId !== liveEventId) {
        const liveEventPath = matchPath(`/events/${liveEventId}`, pathname);
        if (liveEventPath) {
          navigatingToIdRef.current = null;
          return;
        }

        // Already in flight to this target — don't re-dispatch.
        if (navigatingToIdRef.current === liveEventId) return;
        navigatingToIdRef.current = liveEventId;

        console.log(
          '[useChannelLiveEventNavigation] - Navigating to next live event'
        );

        const v = valuesRef.current;
        if (!v.liveEvent) return;
        dispatch(
          navigateToEvent(
            v.liveEvent,
            v.loadAudio,
            v.stop,
            v.navigate,
            v.subscribeToBroadcast,
            isListenerAppWebview
          )
        );
      }
    },
    [
      eventId,
      eventIsUpcoming,
      liveEventId,
      liveEventIsFallback,
      pathname,
      isBrowserEmbed,
      isCreatorAppWebview,
      currentEventIsLive,
      dispatch,
      channelFallbackEnabled,
      isListenerAppWebview,
      fallbackEventLive
    ]
  );
};
